import { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { MessageCircle, X, Send, Loader2 } from "lucide-react";
import { api } from "@/lib/api";

const SUGGESTIONS = [
  "How do I get a price?",
  "How should I measure my cushions?",
  "Can I use my own fabric?",
];

export default function ChatWidget() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([
    { role: "assistant", content: "Hi! I'm the Tanonshop assistant. Ask me about custom cushion covers, sizing, fabrics, or how to get a quote." },
  ]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [sessionId, setSessionId] = useState(() => localStorage.getItem("ts_chat_session") || "");
  const scrollRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, loading, open]);

  const send = async (text) => {
    const msg = (text ?? input).trim();
    if (!msg || loading) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", content: msg }]);
    setLoading(true);
    try {
      const { data } = await api.post("/chat", { session_id: sessionId, message: msg });
      if (data.session_id && data.session_id !== sessionId) {
        setSessionId(data.session_id);
        localStorage.setItem("ts_chat_session", data.session_id);
      }
      setMessages((m) => [...m, { role: "assistant", content: data.reply }]);
    } catch (e) {
      setMessages((m) => [...m, { role: "assistant", content: "Sorry, I'm having trouble right now. Please email tanonshop@gmail.com or use the Custom Order form." }]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <button
        data-testid="chat-toggle-btn"
        onClick={() => setOpen((o) => !o)}
        aria-label="Open chat assistant"
        className="fixed bottom-6 right-6 z-[60] flex h-14 w-14 items-center justify-center rounded-full bg-teal-primary text-paper shadow-[0_8px_30px_rgba(11,43,38,0.35)] transition-all duration-200 hover:bg-teal-hover hover:-translate-y-0.5"
      >
        {open ? <X size={24} /> : <MessageCircle size={24} />}
      </button>

      {open && (
        <div
          data-testid="chat-window"
          className="fixed bottom-24 right-6 z-[60] flex h-[560px] max-h-[calc(100vh-8rem)] w-[calc(100vw-3rem)] max-w-[400px] flex-col overflow-hidden rounded-sm border border-teal-anchor/15 bg-paper shadow-[0_20px_60px_rgba(11,43,38,0.25)]"
        >
          <div className="grain relative bg-teal-anchor px-5 py-4">
            <div className="relative z-10">
              <p className="font-serif text-lg tracking-tight text-paper">Tanonshop Assistant</p>
              <p className="text-xs text-paper/60">Ask about quotes, sizing & fabrics</p>
            </div>
          </div>

          <div ref={scrollRef} data-testid="chat-messages" className="flex-1 space-y-4 overflow-y-auto px-4 py-5">
            {messages.map((m, i) => (
              <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                <div
                  data-testid={`chat-msg-${m.role}`}
                  className={`max-w-[85%] rounded-sm px-4 py-2.5 text-sm leading-relaxed ${
                    m.role === "user"
                      ? "bg-teal-primary text-paper"
                      : "border border-teal-anchor/12 bg-white text-teal-anchor"
                  }`}
                >
                  {m.content}
                </div>
              </div>
            ))}
            {loading && (
              <div className="flex justify-start">
                <div className="flex items-center gap-2 rounded-sm border border-teal-anchor/12 bg-white px-4 py-2.5 text-sm text-teal-anchor/60">
                  <Loader2 size={15} className="animate-spin" /> Typing…
                </div>
              </div>
            )}

            {messages.length === 1 && !loading && (
              <div className="flex flex-col items-start gap-2 pt-2">
                {SUGGESTIONS.map((s) => (
                  <button
                    key={s}
                    data-testid="chat-suggestion"
                    onClick={() => send(s)}
                    className="rounded-full border border-teal-anchor/20 px-3.5 py-1.5 text-xs text-teal-anchor/80 transition-colors duration-200 hover:border-teal-primary hover:text-teal-primary"
                  >
                    {s}
                  </button>
                ))}
              </div>
            )}
          </div>

          <div className="border-t border-teal-anchor/10 p-3">
            <button
              data-testid="chat-quote-cta"
              onClick={() => { setOpen(false); navigate("/custom-order"); }}
              className="mb-2 w-full rounded-sm bg-teal-anchor px-4 py-2.5 text-xs font-semibold uppercase tracking-[0.15em] text-paper transition-colors duration-200 hover:bg-teal-hover"
            >
              Request a Custom Quote
            </button>
            <form
              onSubmit={(e) => { e.preventDefault(); send(); }}
              className="flex items-center gap-2 rounded-sm border border-teal-anchor/20 bg-white px-3 py-2"
            >
              <input
                data-testid="chat-input"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="Type your question…"
                className="flex-1 bg-transparent text-sm text-teal-anchor outline-none placeholder:text-teal-anchor/40"
              />
              <button type="submit" data-testid="chat-send-btn" disabled={loading || !input.trim()} className="text-teal-primary transition-opacity disabled:opacity-40" aria-label="Send">
                <Send size={18} />
              </button>
            </form>
          </div>
        </div>
      )}
    </>
  );
}
